import { failure, isFailure, isOk, isPending, ok, pending, Result } from "@/common/common";
import {
  DCEvent,
  EventDispatcher,
  HttpEvent,
  HttpEventCallbacks,
  UpdateStoreEvent,
  WithStoreEvent,
} from "@/events/eventsDomain";
import { RootStoreData } from "@/events/rootStoreData";
import { TMessage } from "@/i18n/tt";
import {
  errorCount,
  gridColumns,
  gridRowSource,
  indexIssues,
  SpreadsheetImportGrid,
  SpreadsheetImportGridProps,
  SpreadsheetImportIssue,
  SpreadsheetImportRow,
  spreadsheetRejectionMessage,
  takenElsewhere,
  withCell,
  withColumnField,
  withRow,
} from "./spreadsheetImportDomain";

export type SpreadsheetImportSetup = {
  headers: string[];
  countryCodes: string[];
  fieldCountryCodes: string[];
};

type NoExtra = Record<never, never>;

export type SpreadsheetImportState<Setup, Response, Extra = NoExtra> = {
  accountId: string;
  setupResult: Result<Setup, TMessage>;
  rows: SpreadsheetImportRow[];
  columns: (string | undefined)[];
  errorsOnly: boolean;
  selectedOnly: boolean;
  revision: number;
  validation?: Result<Response>;
  resultRevision?: number;
  importResult?: Result<Response>;
  afterImport: DCEvent[];
} & Extra;

type HttpCall<Request, Response> = (
  accountId: string,
  request: Request,
  params: HttpEventCallbacks<Response>,
) => HttpEvent<Response>;

export type SpreadsheetImportFlow<Setup, Request, Response, Extra = NoExtra> = {
  set: (
    change: (
      state: SpreadsheetImportState<Setup, Response, Extra> | undefined,
    ) => SpreadsheetImportState<Setup, Response, Extra> | undefined,
  ) => UpdateStoreEvent;
  stateOf: (root: RootStoreData) => SpreadsheetImportState<Setup, Response, Extra> | undefined;
  initial: Extra;
  requestOf: (state: SpreadsheetImportState<Setup, Response, Extra>) => Request;
  validate: HttpCall<Request, Response>;
  import: HttpCall<Request, Response>;
};

export type SpreadsheetImportLoaded<Setup> = {
  setup: Setup;
  rows: SpreadsheetImportRow[];
  columns: (string | undefined)[];
};

const updateState = <Setup, Request, Response, Extra>(
  flow: SpreadsheetImportFlow<Setup, Request, Response, Extra>,
  change: (
    state: SpreadsheetImportState<Setup, Response, Extra>,
  ) => SpreadsheetImportState<Setup, Response, Extra>,
): UpdateStoreEvent => flow.set((state) => state && change(state));

const validate = <Setup, Request, Response, Extra>(
  flow: SpreadsheetImportFlow<Setup, Request, Response, Extra>,
  state: SpreadsheetImportState<Setup, Response, Extra>,
): DCEvent[] => {
  const revision = state.revision;
  const applies = (current: SpreadsheetImportState<Setup, Response, Extra>) =>
    current.revision === revision;
  return [
    flow.validate(state.accountId, flow.requestOf(state), {
      onSuccess: (res) => [
        updateState(flow, (current) =>
          applies(current)
            ? { ...current, validation: ok(res.data), resultRevision: revision }
            : current,
        ),
      ],
      onFailure: () => [
        updateState(flow, (current) =>
          applies(current)
            ? { ...current, validation: failure(), resultRevision: revision }
            : current,
        ),
      ],
    }),
  ];
};

export const openSpreadsheetImport = <Setup, Request, Response, Extra, Sheet>(
  flow: SpreadsheetImportFlow<Setup, Request, Response, Extra>,
  accountId: string,
  files: File[] | null,
  afterImport: DCEvent[],
  read: (accountId: string, file: File, params: HttpEventCallbacks<Sheet>) => HttpEvent<Sheet>,
  loaded: (sheet: Sheet) => SpreadsheetImportLoaded<NoInfer<Setup>>,
): DCEvent[] => {
  const file = files?.[0];
  if (!file) {
    return [];
  }
  return [
    flow.set(() => ({
      accountId,
      setupResult: pending(),
      rows: [],
      columns: [],
      errorsOnly: false,
      selectedOnly: false,
      revision: 0,
      afterImport,
      ...flow.initial,
    })),
    read(accountId, file, {
      onSuccess: (res, rootState) => {
        const current = flow.stateOf(rootState);
        if (!current) {
          return [];
        }
        const { setup, rows, columns } = loaded(res.data);
        const next = {
          ...current,
          setupResult: ok(setup),
          rows,
          columns,
          revision: current.revision + 1,
        };
        return [updateState(flow, () => next), ...validate(flow, next)];
      },
      onFailure: (err) => [
        updateState(flow, (state) => ({
          ...state,
          setupResult: failure(spreadsheetRejectionMessage(err)),
        })),
      ],
    }),
  ];
};

export const changeAndValidate = <Setup, Request, Response, Extra>(
  flow: SpreadsheetImportFlow<Setup, Request, Response, Extra>,
  change: (
    state: SpreadsheetImportState<Setup, Response, Extra>,
  ) => SpreadsheetImportState<Setup, Response, Extra>,
): WithStoreEvent => ({
  type: "with-store",
  events: (root) => {
    const state = flow.stateOf(root);
    const changed = state && change(state);
    if (!state || !changed || changed === state) {
      return [];
    }
    const next = { ...changed, revision: state.revision + 1 };
    return [updateState(flow, () => next), ...validate(flow, next)];
  },
});

const submitImport = <Setup, Request, Response, Extra>(
  flow: SpreadsheetImportFlow<Setup, Request, Response, Extra>,
  state: SpreadsheetImportState<Setup, Response, Extra>,
): DCEvent[] => [
  updateState(flow, (current) => ({ ...current, importResult: pending() })),
  flow.import(state.accountId, flow.requestOf(state), {
    onSuccess: (res) => [
      updateState(flow, (current) => ({ ...current, importResult: ok(res.data) })),
      ...state.afterImport,
    ],
    onFailure: () => [updateState(flow, (current) => ({ ...current, importResult: failure() }))],
  }),
];

export const setupOf = <Setup extends SpreadsheetImportSetup>(
  state: SpreadsheetImportState<Setup, unknown>,
  noSetup: Setup,
): Setup => (isOk(state.setupResult) ? state.setupResult.data : noSetup);

export type SpreadsheetImportFlowProps<Setup> = SpreadsheetImportGridProps & {
  setupResult: Result<Setup, TMessage>;
  isImporting: boolean;
  importFailed: boolean;
  onImport: () => void;
  onClose: () => void;
};

export const prepSpreadsheetImportFlow = <
  Setup extends SpreadsheetImportSetup,
  Request,
  Response,
  Extra,
>(
  flow: SpreadsheetImportFlow<Setup, Request, Response, Extra>,
  state: SpreadsheetImportState<Setup, Response, Extra>,
  setup: Setup,
  grid: SpreadsheetImportGrid,
  issues: SpreadsheetImportIssue[],
  dispatch: EventDispatcher,
): SpreadsheetImportFlowProps<Setup> => {
  const index = indexIssues(issues);
  const columns = gridColumns(grid, index);
  return {
    setupResult: state.setupResult,
    fieldsByKey: grid.fieldsByKey,
    countryCodes: setup.countryCodes,
    fieldCountryCodes: setup.fieldCountryCodes,
    gridColumns: columns,
    gridRows: gridRowSource(grid, index, columns),
    rowCount: state.rows.length,
    errorCount: errorCount(issues),
    errorsOnly: state.errorsOnly,
    selectedOnly: state.selectedOnly,
    isValidating: state.resultRevision !== state.revision,
    isImporting: isPending(state.importResult),
    importFailed: isFailure(state.importResult),
    isTakenElsewhere: takenElsewhere(state.columns),
    onSelectField: (column, key) =>
      dispatch([changeAndValidate(flow, (current) => withColumnField(current, column, key))]),
    onChangeCountryCode: (row, countryCode) =>
      dispatch([changeAndValidate(flow, (current) => withRow(current, row, { countryCode }))]),
    onChangeOrgNo: (row, orgNo) =>
      dispatch([changeAndValidate(flow, (current) => withRow(current, row, { orgNo }))]),
    onChangeCell: (row, column, text) =>
      dispatch([changeAndValidate(flow, (current) => withCell(current, row, column, text))]),
    onToggleErrorsOnly: () =>
      dispatch([updateState(flow, (current) => ({ ...current, errorsOnly: !current.errorsOnly }))]),
    onToggleSelectedOnly: () =>
      dispatch([
        updateState(flow, (current) => ({ ...current, selectedOnly: !current.selectedOnly })),
      ]),
    onImport: () => dispatch(submitImport(flow, state)),
    onClose: () => dispatch([flow.set(() => undefined)]),
  };
};
